/**
 * @fileoverview Estilos da página de resultados do fluxo. Monocromática, no
 * padrão visual da tela de Ordens de serviço; cor só para estado (ativo, em
 * andamento, falhou) e para a linha do gráfico.
 */

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#4b5563";
  const textFaint = isDark ? "#718096" : "#6b7280";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.12)";
  const inputBorder = isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.18)";
  const surface = isDark ? "rgba(17, 19, 24, 0.85)" : "#ffffff";
  const rowHover = isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.035)";
  const chipBg = isDark ? "rgba(148,163,184,0.14)" : "rgba(71,85,105,0.08)";
  const chipText = isDark ? "#cbd5e1" : "#334155";
  const skeleton = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)";

  // Cores de estado: mais escuras no claro para ler sobre branco.
  const ok = isDark ? "#22c55e" : "#15803d";
  const warn = isDark ? "#f59e0b" : "#b45309";
  const danger = isDark ? "#ef4444" : "#b91c1c";

  const dot = (color) => ({ width: 8, height: 8, borderRadius: 4, background: color, flexShrink: 0 });

  return {
    page: {
      padding: "28px 32px 48px",
      minHeight: "100%",
      // A página é item de um contêiner flex: sem isto ela cresce até caber o
      // maior texto sem quebra (nome de contato) e estoura a tela no celular.
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      maxWidth: 1320,
      margin: "0 auto",
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
      [theme.breakpoints.down("sm")]: { padding: "20px 16px 32px" },
    },

    // Cabeçalho
    backLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: 0,
      border: "none",
      background: "transparent",
      color: textSecondary,
      fontSize: 12,
      cursor: "pointer",
      marginBottom: 10,
      "&:hover": { color: textPrimary },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      marginBottom: 20,
    },
    headerMain: { minWidth: 0, flex: "1 1 320px" },
    titleRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
    title: { fontSize: 22, fontWeight: 600, color: textPrimary, wordBreak: "break-word" },
    statusChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 500,
      background: chipBg,
      color: chipText,
      whiteSpace: "nowrap",
    },
    dotOk: dot(ok),
    dotOff: dot(textFaint),
    dotRunning: dot(warn),
    dotError: dot(danger),
    description: { fontSize: 13, color: textSecondary, marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" },
    descriptionEmpty: { color: textFaint },
    textBtn: {
      padding: 0,
      border: "none",
      background: "transparent",
      color: textSecondary,
      fontSize: 12,
      cursor: "pointer",
      textDecoration: "underline",
      textUnderlineOffset: 2,
      marginLeft: 8,
      "&:hover": { color: textPrimary },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    since: { fontSize: 12, color: textFaint, marginTop: 8 },
    headerActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
    primaryBtn: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${textPrimary}`,
      background: textPrimary,
      color: isDark ? "#111318" : "#ffffff",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      whiteSpace: "nowrap",
      "&:hover": { opacity: 0.9 },
      "&:disabled": { opacity: 0.45, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    secondaryBtn: {
      height: 38,
      padding: "0 14px",
      borderRadius: 10,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textPrimary,
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      whiteSpace: "nowrap",
      "&:hover": { background: rowHover },
      "&:disabled": { opacity: 0.45, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    smallBtn: { height: 32, fontSize: 12, padding: "0 12px", borderRadius: 8 },

    // Período
    toolbar: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 },
    segmented: {
      display: "inline-flex",
      border: `1px solid ${inputBorder}`,
      borderRadius: 10,
      overflow: "hidden",
      background: surface,
    },
    segment: {
      height: 34,
      padding: "0 14px",
      border: "none",
      background: "transparent",
      color: textSecondary,
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      "&:not(:last-child)": { borderRight: `1px solid ${border}` },
      "&:hover": { background: rowHover, color: textPrimary },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: -2 },
    },
    segmentActive: {
      background: textPrimary,
      color: isDark ? "#111318" : "#ffffff",
      "&:hover": { background: textPrimary, color: isDark ? "#111318" : "#ffffff" },
    },
    dateRange: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: textSecondary },
    dateInput: {
      height: 34,
      padding: "0 10px",
      borderRadius: 10,
      border: `1px solid ${inputBorder}`,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      color: textPrimary,
      fontSize: 12.5,
      outline: "none",
      colorScheme: isDark ? "dark" : "light",
      "&:focus": { borderColor: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)", boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)" },
    },
    toolbarRight: { marginLeft: "auto", fontSize: 12, color: textFaint, display: "inline-flex", alignItems: "center", gap: 10 },

    // Indicadores
    metrics: {
      display: "grid",
      gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
      gap: 12,
      marginBottom: 12,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    metric: {
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "14px 16px",
      minWidth: 0,
    },
    metricLabel: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      fontWeight: 500,
      color: textSecondary,
    },
    helpIcon: { color: textFaint, cursor: "help", display: "inline-flex", "&:focus-visible": { outline: `2px solid ${textPrimary}`, borderRadius: 8 } },
    metricValue: {
      fontSize: 26,
      fontWeight: 600,
      color: textPrimary,
      lineHeight: 1.15,
      marginTop: 8,
      fontVariantNumeric: "tabular-nums",
    },
    metricUnavailable: { fontSize: 15, fontWeight: 600, color: textSecondary, marginTop: 12, lineHeight: 1.3 },
    metricSub: { fontSize: 12, color: textSecondary, marginTop: 6, lineHeight: 1.4, minHeight: 17 },
    metricSubFaint: { color: textFaint },
    summaryLine: { display: "flex", flexWrap: "wrap", gap: "6px 18px", fontSize: 12, color: textSecondary, margin: "0 2px 20px" },
    summaryItem: { display: "inline-flex", alignItems: "center", gap: 6 },

    // Seções
    section: {
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "16px 18px",
      marginBottom: 20,
    },
    sectionHeader: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 },
    sectionTitle: { fontSize: 14, fontWeight: 600, color: textPrimary },
    sectionHint: { fontSize: 12, color: textFaint },

    // Gráfico
    chartBox: { width: "100%", height: 240 },
    chartEmpty: {
      height: 240,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: textSecondary,
      fontSize: 13,
      textAlign: "center",
    },
    chartTooltip: {
      background: isDark ? "#1c1e23" : "#ffffff",
      border: `1px solid ${border}`,
      borderRadius: 8,
      padding: "8px 10px",
      boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(15,23,42,0.12)",
      fontSize: 12,
      color: textPrimary,
    },
    chartTooltipDay: { color: textSecondary, marginBottom: 2 },
    chartTooltipValue: { fontWeight: 600, fontVariantNumeric: "tabular-nums" },
    srOnly: {
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      whiteSpace: "nowrap",
      border: 0,
    },

    // Histórico
    filters: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 },
    searchWrap: { position: "relative", flex: "1 1 260px", maxWidth: 380 },
    searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: textFaint },
    input: {
      width: "100%",
      height: 40,
      padding: "0 12px 0 36px",
      fontSize: 13,
      borderRadius: 14,
      border: `1px solid ${isDark ? "rgba(91, 124, 255, 0.15)" : "rgba(15, 23, 42, 0.2)"}`,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      color: textPrimary,
      outline: "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": { borderColor: isDark ? "rgba(91, 124, 255, 0.25)" : "rgba(61, 92, 253, 0.4)" },
      "&:focus": {
        borderColor: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)",
        boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
      },
      "&::placeholder": { color: textFaint },
    },
    filterSelect: { minWidth: 180, "& .MuiSelect-select": { fontSize: 13 } },
    dialogSelect: { "& .MuiSelect-select": { fontSize: 13.5 }, "& .MuiFormHelperText-root": { fontSize: 12 } },
    tableWrap: { border: `1px solid ${border}`, borderRadius: 12, overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 860 },
    th: {
      textAlign: "left",
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: textSecondary,
      padding: "10px 14px",
      borderBottom: `1px solid ${border}`,
      whiteSpace: "nowrap",
    },
    tr: {
      cursor: "pointer",
      "&:hover": { background: rowHover },
      "&:not(:last-child) td": { borderBottom: `1px solid ${border}` },
      "&:focus-visible": { outline: "2px solid #4f7cff", outlineOffset: -2 },
    },
    td: { fontSize: 13, color: textPrimary, padding: "11px 14px", verticalAlign: "top" },
    tdMuted: { fontSize: 12, color: textSecondary, marginTop: 2 },
    tdError: { fontSize: 12, color: danger, marginTop: 2, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    nowrap: { whiteSpace: "nowrap" },
    stateChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "2px 9px",
      borderRadius: 10,
      fontSize: 11.5,
      fontWeight: 500,
      background: chipBg,
      color: chipText,
      whiteSpace: "nowrap",
    },
    chip: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 500,
      background: chipBg,
      color: chipText,
      whiteSpace: "nowrap",
    },
    empty: {
      padding: "48px 20px",
      textAlign: "center",
      color: textSecondary,
      fontSize: 13,
      border: `1px dashed ${border}`,
      borderRadius: 12,
    },
    emptyTitle: { fontWeight: 600, color: textPrimary, marginBottom: 4, fontSize: 14 },
    loadMore: { display: "flex", justifyContent: "center", marginTop: 14 },
    cardList: { display: "flex", flexDirection: "column", gap: 8 },
    mCard: {
      display: "block",
      width: "100%",
      textAlign: "left",
      background: "transparent",
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      color: textPrimary,
      cursor: "pointer",
      "&:active": { background: rowHover },
    },
    mCardTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 },
    mCardName: { fontSize: 14, fontWeight: 500, color: textPrimary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

    // Carregando e erro
    skeleton: {
      borderRadius: 8,
      background: skeleton,
      animation: "$pulse 1.4s ease-in-out infinite",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.45 },
    },
    banner: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 14px",
      borderRadius: 10,
      fontSize: 13,
      lineHeight: 1.45,
      marginBottom: 16,
      border: "1px solid",
    },
    bannerError: {
      background: isDark ? "rgba(239,68,68,0.08)" : "#fef2f2",
      borderColor: isDark ? "rgba(239,68,68,0.35)" : "#ef4444",
      color: isDark ? "#fca5a5" : "#7f1d1d",
    },
    bannerNeutral: {
      background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
      borderColor: border,
      color: textSecondary,
    },

    // Painel da execução
    drawerPaper: {
      width: 760,
      maxWidth: "100vw",
      background: isDark ? "#14171d" : "#ffffff",
      color: textPrimary,
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
    },
    drawerHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      padding: "18px 20px",
      borderBottom: `1px solid ${border}`,
    },
    drawerTitle: { fontSize: 18, fontWeight: 700, color: textPrimary, wordBreak: "break-word" },
    drawerSub: { fontSize: 12, color: textSecondary, marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
    drawerBody: { padding: "4px 20px 28px", overflowY: "auto", flex: 1 },
    block: { padding: "16px 0", "&:not(:last-child)": { borderBottom: `1px solid ${border}` } },
    blockTitle: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: textSecondary,
      marginBottom: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    blockNote: { fontSize: 12, color: textSecondary, textTransform: "none", letterSpacing: 0, fontWeight: 400 },
    facts: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: "12px 16px",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    factLabel: { fontSize: 11, color: textSecondary, marginBottom: 2 },
    factValue: { fontSize: 13, color: textPrimary, fontVariantNumeric: "tabular-nums" },
    errorBox: {
      padding: "10px 12px",
      borderRadius: 8,
      fontSize: 12.5,
      lineHeight: 1.5,
      border: "1px solid",
      background: isDark ? "rgba(239,68,68,0.08)" : "#fef2f2",
      borderColor: isDark ? "rgba(239,68,68,0.35)" : "#fca5a5",
      color: isDark ? "#fca5a5" : "#7f1d1d",
      wordBreak: "break-word",
    },
    errorMeta: { fontSize: 11, opacity: 0.85, marginTop: 4 },
    // Envio que não saiu de propósito (descadastro, prazo): motivo, não erro.
    reasonBox: {
      padding: "10px 12px",
      borderRadius: 8,
      fontSize: 12.5,
      lineHeight: 1.5,
      border: `1px solid ${border}`,
      background: isDark ? "rgba(255,255,255,0.03)" : "#f7f8fc",
      color: textPrimary,
      wordBreak: "break-word",
    },
    pathBox: {
      height: 320,
      border: `1px solid ${border}`,
      borderRadius: 10,
      overflow: "hidden",
      display: "flex",
      background: isDark ? "#0e1017" : "#f7f8fc",
      // Os controles do editor ficam ancorados ao painel de logs dele; aqui, no canto.
      "& .react-flow__controls": { right: "10px !important", bottom: "10px !important", left: "auto !important" },
    },
    legend: { display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 12, color: textSecondary, marginTop: 10 },
    legendItem: { display: "inline-flex", alignItems: "center", gap: 6 },
    legendPending: { ...dot("transparent"), border: `1px solid ${textFaint}` },
    list: { display: "flex", flexDirection: "column" },
    listRow: {
      display: "grid",
      gridTemplateColumns: "76px 1fr",
      gap: 12,
      padding: "8px 0",
      fontSize: 13,
      "&:not(:last-child)": { borderBottom: `1px dashed ${border}` },
    },
    listTime: { fontSize: 12, color: textSecondary, fontVariantNumeric: "tabular-nums", paddingTop: 1 },
    listMain: { minWidth: 0, color: textPrimary, wordBreak: "break-word" },
    listMeta: { fontSize: 12, color: textSecondary, marginTop: 2 },
    listError: { fontSize: 12, color: danger, marginTop: 2 },
    messageBody: {
      fontSize: 13,
      lineHeight: 1.5,
      color: textPrimary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      display: "-webkit-box",
      WebkitLineClamp: 4,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    messageDeleted: { fontSize: 13, color: textFaint, fontStyle: "italic" },
    link: { color: textPrimary, textDecoration: "underline", textUnderlineOffset: 2, wordBreak: "break-all" },

    // Medição (diálogo)
    dialogPaper: {
      borderRadius: 14,
      background: isDark ? "#14171d" : "#ffffff",
      color: textPrimary,
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
    },
    dialogTitle: { fontSize: 17, fontWeight: 700, color: textPrimary, padding: "18px 22px 6px" },
    dialogBody: { padding: "6px 22px 8px", display: "flex", flexDirection: "column", gap: 4 },
    dialogActions: { padding: "10px 22px 18px", display: "flex", justifyContent: "flex-end", gap: 8 },
    formGroup: { padding: "14px 0", "&:not(:last-child)": { borderBottom: `1px solid ${border}` } },
    formTitle: { fontSize: 13.5, fontWeight: 600, color: textPrimary },
    formHint: { fontSize: 12, color: textSecondary, lineHeight: 1.5, marginTop: 4 },
    formRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" },
    formLabel: { fontSize: 12.5, fontWeight: 500, color: textPrimary },
    textField: {
      flex: "1 1 220px",
      height: 38,
      padding: "0 12px",
      fontSize: 13,
      borderRadius: 10,
      border: `1px solid ${inputBorder}`,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      color: textPrimary,
      outline: "none",
      "&:focus": { borderColor: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)", boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)" },
      "&::placeholder": { color: textFaint },
      "&:disabled": { opacity: 0.6 },
    },
    textArea: {
      width: "100%",
      minHeight: 96,
      padding: "10px 12px",
      fontSize: 13,
      lineHeight: 1.5,
      borderRadius: 10,
      border: `1px solid ${inputBorder}`,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      color: textPrimary,
      outline: "none",
      resize: "vertical",
      "&:focus": { borderColor: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)", boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)" },
      "&::placeholder": { color: textFaint },
    },
    counter: { fontSize: 11, color: textFaint, textAlign: "right", marginTop: 4 },
    hostChips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
    hostChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 6px 3px 10px",
      borderRadius: 10,
      fontSize: 12,
      background: chipBg,
      color: chipText,
    },
    chipRemove: {
      display: "inline-flex",
      padding: 2,
      border: "none",
      borderRadius: 6,
      background: "transparent",
      color: "inherit",
      cursor: "pointer",
      "&:hover": { background: rowHover },
    },
    codeBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginTop: 6,
      padding: "8px 10px",
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
      fontSize: 12,
      color: textPrimary,
    },
    codeText: { flex: 1, minWidth: 0, wordBreak: "break-all", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace !important" },
    switchRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10 },
  };
});

export default useStyles;
